import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Spinner } from 'react-bootstrap';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import Swal from 'sweetalert2';
import { SellerDetailsContactType } from '../../../structures/models/seller';
import { SellerContactFormData } from '../../../structures/models/listings';
import { getSellerContactFormData, resendEmailVerificationLink } from '../../../utils/ajax/sellerContactFormAjax';
import { GOOGLE_RECAPTCHA_KEY } from '../../../utils/constants';
import SellerContactForm from './SellerContactForm';
import SellerDetailsContact from './SellerDetailsContact';
import SellerContactFormAlert from './SellerContactFormAlert';

interface Props extends SellerContactFormData {
  sellerContactId: number,
}

const SellerContact: React.FC<Props> = ({
  sellerContactId,
  listing,
  currentUser,
  displayContext,
}) => {
  const [hasPressed, setHasPressed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [tfaRoute, setTfaRoute] = useState('');
  const [sellerContactInfo, setSellerContactInfo] = useState<
    SellerDetailsContactType|undefined
  >(undefined);
  const [showResendEmailButton, setShowResendEmailButton] = useState(false);

  const fetchSellerContactFormData = useCallback(() => {
    if (hasPressed) {
      return;
    }
    getSellerContactFormData(sellerContactId, listing?.id, listing?.type)
      .then((res) => {
        setLoading(false);
        setErrorMessage('');
        setHasPressed(true);
        setSellerContactInfo(res);
        setShowResendEmailButton(false);
        setTfaRoute('');
      })
      .catch((error) => {
        // Add message
        if (error.response?.data?.errors?.email_verified_at) {
          const message = Array.isArray(error.response?.data?.errors?.email_verified_at)
            ? error.response?.data?.errors?.email_verified_at.join(' ')
            : error.response?.data?.errors?.email_verified_at;
          setTfaRoute('');
          setErrorMessage(message);
          setShowResendEmailButton(true);
        } else if (error.response?.data?.errors?.limit_exceeded) {
          const message = Array.isArray(error.response?.data?.errors?.limit_exceeded)
            ? error.response?.data?.errors?.limit_exceeded.join(' ')
            : error.response?.data?.errors?.limit_exceeded;
          setTfaRoute(error.response?.data?.errors?.tfa_route ?? '');
          setErrorMessage(message);
          setShowResendEmailButton(false);
        } else {
          setTfaRoute('');
          setErrorMessage('Cannot contact seller at this time.');
          setShowResendEmailButton(false);
        }
        setLoading(false);
        setSellerContactInfo(undefined);
      });
  }, [hasPressed]);

  useEffect(() => {
    const contactButtons = document.querySelectorAll('.js-contact-breeder-button');
    contactButtons.forEach((button) => {
      button.addEventListener('click', fetchSellerContactFormData);
    });
    return () => {
      contactButtons.forEach((button) => {
        button.removeEventListener('click', fetchSellerContactFormData);
      });
    };
  }, [fetchSellerContactFormData]);

  const resendEmail = () => {
    setLoading(true);
    resendEmailVerificationLink()
      .then((res) => {
        Swal.fire(
          'Success',
          res || 'Verification link sent to your email address.',
          'success',
        ).then(() => {
          setLoading(false);
          // Reload page if email link sent successfully
          window.location.reload();
        });
      })
      .catch((error) => {
        setErrorMessage('Something went wrong; failed to send new email verification link.');
        setLoading(false);
      });
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-center m-4">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (!sellerContactInfo) {
    return (
      <SellerContactFormAlert
        errorMessage={errorMessage}
        tfaRoute={tfaRoute}
        showResendEmailButton={showResendEmailButton}
        resendEmail={resendEmail}
      />
    );
  }

  return (
    <>
      <SellerDetailsContact
        profilePhotoUrl={sellerContactInfo.profilePhotoUrl}
        sellerName={sellerContactInfo.sellerName}
        contact={sellerContactInfo.contact}
      />
      {sellerContactInfo.contact.primaryCanBeEmailed && (
        <GoogleReCaptchaProvider reCaptchaKey={GOOGLE_RECAPTCHA_KEY || ''}>
          <SellerContactForm
            name={sellerContactInfo.contact.name}
            sellerId={sellerContactInfo.sellerId}
            listing={listing}
            currentUser={currentUser}
            displayContext={displayContext}
          />
        </GoogleReCaptchaProvider>
      )}
    </>
  );
};

const elem = document.getElementById('seller-contact-form');

if (elem) {
  const sellerContactIdAttribute = elem.getAttribute('data-seller-contact-id');
  const listingAttribute = elem.getAttribute('data-listing');
  const currentUserAttribute = elem.getAttribute('data-current-user');
  const displayContextAttribute = elem.getAttribute('data-display-context');

  const sellerContactId = sellerContactIdAttribute ? JSON.parse(sellerContactIdAttribute) : null;
  const listing = listingAttribute ? JSON.parse(listingAttribute) : null;
  const currentUser = currentUserAttribute ? JSON.parse(currentUserAttribute) : null;
  const displayContext = displayContextAttribute || undefined;

  ReactDOM.render(
    <SellerContact
      sellerContactId={sellerContactId}
      listing={listing}
      currentUser={currentUser}
      displayContext={displayContext}
    />,
    elem,
  );
}
